/**
 * Interface for URL validation results
 */
export interface ValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * Validates if a URL is correct according to the following criteria:
 * - Must use HTTPS protocol
 * - Must follow the standard URL structure
 * - Must pass basic security checks
 *
 * @param url - The URL to validate
 * @returns ValidationResult - Object containing validation status and error message if invalid
 */
export function validateUrl(url: string): ValidationResult {
  if (!url) {
    return {
      isValid: false,
      errorMessage: 'URL не может быть пустым',
    };
  }

  try {
    // Create URL object to validate structure
    const urlObj = new URL(url);

    // Check if protocol is HTTPS
    if (urlObj.protocol !== 'https:') {
      return {
        isValid: false,
        errorMessage: 'URL должен использовать протокол HTTPS',
      };
    }

    // Check if hostname exists and is valid
    if (!urlObj.hostname || urlObj.hostname.length === 0) {
      return {
        isValid: false,
        errorMessage: 'URL должен содержать действительное доменное имя',
      };
    }

    // Check for at least one dot in hostname (basic domain validation)
    if (!urlObj.hostname.includes('.')) {
      return {
        isValid: false,
        errorMessage: 'Доменное имя должно содержать как минимум одну точку',
      };
    }

    // Security checks
    // Prevent javascript: protocol disguised as part of the URL
    if (url.toLowerCase().includes('javascript:')) {
      return {
        isValid: false,
        errorMessage: 'URL содержит потенциально опасный контент (javascript:)',
      };
    }

    // Prevent data: protocol disguised as part of the URL
    if (url.toLowerCase().includes('data:')) {
      return {
        isValid: false,
        errorMessage: 'URL содержит потенциально опасный контент (data:)',
      };
    }

    // Prevent potential XSS vectors
    if (url.includes('<') || url.includes('>') || url.includes('script')) {
      return {
        isValid: false,
        errorMessage: 'URL содержит потенциально опасные символы',
      };
    }

    return {
      isValid: true,
      errorMessage: null,
    };
  } catch (error) {
    // URL constructor will throw if the URL is invalid
    return {
      isValid: false,
      errorMessage: 'Неверный формат URL',
    };
  }
}
